import { inject, Injectable } from '@angular/core';
import { assert, mapAsKeys } from '@sama/js-core';
import { mapKeys } from '@sama/micro-dash';
import { isEqual, keys, mapValues, omitBy, size } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { getShapesForEventTracking } from '../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import {
  determineMethod,
  determineResultingIndexes,
} from '../../../../../cross-team-shared/annotation/shared/utils/selection-utils';
import { EventTrackingValueWithSource } from '../../../../../cross-team-shared/misc-utils';
import { keys as numberKeys } from '../../../../../cross-team-shared/misc-utils/keys';
import { getOrThrow } from '../../../../../cross-team-shared/misc-utils/strict-record';
import { EventTrackingService } from '../../../../../shared/event-tracking/event-tracking.service';
import { IN_PROGRESS_INDEX } from '../../../../../team-apis/core/reserved-shape-indexes';
import { Selection } from '../../../../h3ml/state/fusion-state';
import { outputInfoStoreToken } from '../../../../h3ml/state/output-info';
import { getShapesFromShapeInfo } from '../../../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { getOutputAnnotationInfoStore } from '../../../../h3ml/state/task-workspace-store-utils';
import { RootUndoService } from '../../../../shared/misc-services/root-undo.service';
import {
  filterSelectionByOutput,
  RootShapeSelectionService,
} from './root-shape-selection.service';

@Injectable()
export class ShapeSelectionService {
  private cachedSelectionWithInProgressShapeByOutput: StrictRecord<
    string,
    Observable<Selection>
  > = {};
  private cachedSelectionByOutput: StrictRecord<string, Observable<Selection>> =
    {};

  private rootShapeSelectionService = inject(RootShapeSelectionService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private outputInfoStore = inject(outputInfoStoreToken);
  private undoService = inject(RootUndoService);
  private eventTrackingService = inject(EventTrackingService);

  getSelectedShapeIndexes() {
    return numberKeys(this.taskWorkspaceStore('shapeSelection').state());
  }

  getSelectionForOutput(includeInProgressIndex = false) {
    const { output } = this.outputInfoStore.state();
    const outputAnnotationInfo =
      this.taskWorkspaceStore.state().outputAnnotationInfo[output.name];
    assert(outputAnnotationInfo, 'output must have annotation info');

    const rootSelection = this.rootShapeSelectionService.getNestedSelection(
      includeInProgressIndex,
      this.taskWorkspaceStore('shapeSelection').state(),
    );
    return filterSelectionByOutput(outputAnnotationInfo, rootSelection);
  }

  getSelectionForOutput$(includeInProgressIndex = false) {
    const { output } = this.outputInfoStore.state();
    this.cachedSelectionWithInProgressShapeByOutput[output.name] ??=
      this.buildSelectionWithInProgressShape$();
    this.cachedSelectionByOutput[output.name] ??= getOrThrow(
      this.cachedSelectionWithInProgressShapeByOutput,
      output.name,
    ).pipe(
      map((selection) =>
        omitBy(selection, (_, shapeIndex) => +shapeIndex === IN_PROGRESS_INDEX),
      ),
      // selection is used everywhere in the app, there could be tens of
      // subscribers. we don't want to recalculate it everytime
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    return includeInProgressIndex
      ? this.cachedSelectionWithInProgressShapeByOutput[output.name]!
      : this.cachedSelectionByOutput[output.name]!;
  }

  getSelectedShapeCount$() {
    // @ts-ignore -- fix me
    return this.getSelectionForOutput$().pipe(
      map((selection) => size(selection)),
    );
  }

  /**
   * Set selection to the desired shape indexes. Does not deselect other shapes
   * if all requested shapes are already selected.
   *
   * @param indexes - The shape indexes that will compose the new selection.
   * @param eventTrackingValue - The event tracking value
   * @returns - true if the selection changed, false otherwise
   */
  setSelection(
    indexes: number[],
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    const currentSelection = this.taskWorkspaceStore('shapeSelection').state();
    // fix me if you change this code

    if (indexes.length && indexes.every((index) => currentSelection[index])) {
      return false;
    }

    const newSelection: Selection = mapAsKeys(indexes, () => true);
    if (isEqual(currentSelection, newSelection)) {
      return false;
    }

    this.taskWorkspaceStore('shapeSelection').set(newSelection);
    this.undoService.currentUndoState.shapeSelection = newSelection;
    if (eventTrackingValue) {
      this.trackSelectionChangeEvent({ ...eventTrackingValue, method: 'set' });
    }
    return true;
  }

  /**
   * Change the selected state of multiple shapes. If at least one shape is
   * deselected, all shapes will be selected. Otherwise, all shapes will be
   * deselected.
   *
   * @param indexes - Indexes of the shapes part of the group to toggle
   * @param eventTrackingValue - The event tracking value
   * @returns - true if shapes are now selected, false otherwise
   */
  toggleSelection(
    indexes: number[],
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    assert(indexes.length > 0, 'indexes must not be empty');
    const initialSelection = this.taskWorkspaceStore('shapeSelection').state();
    const initialIndexes = numberKeys(initialSelection);
    const method = determineMethod(initialIndexes, indexes, 'toggle');

    const resultingIndexes = determineResultingIndexes(
      method,
      initialIndexes,
      indexes,
    );
    const newSelection: Selection = mapAsKeys(resultingIndexes, () => true);
    this.taskWorkspaceStore('shapeSelection').set(newSelection);
    this.undoService.currentUndoState.shapeSelection = newSelection;
    const isNowSelected = newSelection[indexes[0]] ?? false;

    if (eventTrackingValue) {
      this.trackSelectionChangeEvent({
        ...eventTrackingValue,
        method: isNowSelected ? 'add' : 'remove',
      });
    }
    return isNowSelected;
  }

  selectAllObjectless(eventTrackingValue: EventTrackingValueWithSource) {
    const shapesStore = getOutputAnnotationInfoStore(
      this.taskWorkspaceStore,
      this.outputInfoStore.state().output.name,
    )('shapes');

    this.setSelection(numberKeys(shapesStore.state()), eventTrackingValue);
  }

  private trackSelectionChangeEvent(
    eventTrackingValue: EventTrackingValueWithSource & {
      method: string;
    },
  ) {
    this.eventTrackingService.track({
      id: 'change_shape_selection',
      bucket: 'adjustment',
      ...eventTrackingValue,
      resulting_shape_selection: this.getShapesForEventTracking(),
      resulting_object_selection: [],
    });
  }

  private getShapesForEventTracking() {
    const allShapes = getShapesFromShapeInfo(
      this.taskWorkspaceStore.state().outputAnnotationInfo,
    );
    const selectedShapeIndexes = numberKeys(
      this.taskWorkspaceStore.state().shapeSelection,
    );

    return [
      ...getShapesForEventTracking(
        mapKeys(allShapes, (shape) => shape.index),
        selectedShapeIndexes,
      ),
    ];
  }

  private buildSelectionWithInProgressShape$() {
    const { output } = this.outputInfoStore.state();
    return combineLatest([
      this.taskWorkspaceStore('shapeSelection').$,
      // listening on shape changes handles the case where a shape would be
      // created after the selection was updated
      this.taskWorkspaceStore
        .getShapesStore(output.name)
        .$.pipe(distinctUntilChanged((x, y) => isEqual(keys(x), keys(y)))),
      this.taskWorkspaceStore('objectSelection').$,
      this.taskWorkspaceStore('objects').$.pipe(
        map((objects) => mapValues(objects, (object) => object!.parentId)),
        distinctUntilChanged(isEqual),
      ),
    ]).pipe(
      map(() => this.getSelectionForOutput(true)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }
}
